import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { TenantThemeDocument } from '@vipso/shared';

/** Decode membershipId from a JWT access token (org user sessions). */
export function membershipIdFromJwt(token: string | null | undefined): string | null {
  if (!token || token.split('.').length < 2) return null;
  try {
    const payloadPart = token.split('.')[1]!;
    const json =
      typeof atob === 'function'
        ? atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'))
        : Buffer.from(payloadPart, 'base64').toString('utf8');
    const payload = JSON.parse(json) as { membershipId?: string | number };
    return payload.membershipId != null && payload.membershipId !== ''
      ? String(payload.membershipId)
      : null;
  } catch {
    return null;
  }
}


export type TenantTheme = TenantThemeDocument & {
  primaryColor?: string | null;
  secondaryColor?: string | null;
  logoUrl?: string | null;
  fontFamily?: string | null;
  mode?: string | null;
};

export interface TenantConfig {
  defaultContextLogin: boolean;
  loginType: 'OTP' | 'PASSWORD' | string;
  logoFileId?: string | null;
  logoUrl?: string | null;
  maxOrgs?: number | null;
  maxUsers?: number | null;
  enabledModules?: string[] | null;
}

export interface TenantOrgOption {
  id: string;
  name: string;
  uniqueId?: string | null;
  status?: string | null;
}

export interface TenantInfo {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
}

export interface AuthUser {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
}

export interface OrgRef {
  orgId: string;
  name?: string | null;
  membershipId: string;
}

export interface OrgInfo {
  id?: string;
  orgId?: string;
  name?: string;
  status?: string;
  phone?: string;
  uniqueId?: string;
  tenantId?: string;
  timezone?: string;
  logoFileId?: string | null;
  logoUrl?: string | null;
  [key: string]: unknown;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthUser | null;
  org: OrgInfo | null;
  /** Current org membership id */
  membershipId: string | null;
  featureKeys: string[];
  tenant: TenantInfo | null;
  variantName: string | null;
  theme: TenantTheme | null;
  config: TenantConfig | null;
  tenantOrgs: TenantOrgOption[];
  pendingPhone: string | null;
  pendingOrgId: string | null;
  orgs: OrgRef[];
  socketRooms: string[];
  bootstrapped: boolean;
  pendingTaskCount: number;
  operationsLocked: boolean;
}

const initialState: AuthState = {
  accessToken: null,
  refreshToken: null,
  user: null,
  org: null,
  membershipId: null,
  featureKeys: [],
  tenant: null,
  variantName: null,
  theme: null,
  config: null,
  tenantOrgs: [],
  pendingPhone: null,
  pendingOrgId: null,
  orgs: [],
  socketRooms: [],
  bootstrapped: false,
  pendingTaskCount: 0,
  operationsLocked: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setTenantContext(
      state,
      action: PayloadAction<{
        tenant: TenantInfo;
        variantName?: string | null;
        theme?: TenantTheme | null;
        config?: TenantConfig | null;
        tenantOrgs?: TenantOrgOption[];
      }>,
    ) {
      state.tenant = action.payload.tenant;
      state.variantName = action.payload.variantName ?? action.payload.tenant.name;
      state.theme = action.payload.theme ?? null;
      state.config = action.payload.config ?? null;
      state.tenantOrgs = action.payload.tenantOrgs ?? [];
    },
    setPendingPhone(state, action: PayloadAction<string>) {
      state.pendingPhone = action.payload;
    },
    setPendingOrgId(state, action: PayloadAction<string | null>) {
      state.pendingOrgId = action.payload;
    },
    setVerifyOtpResult(
      state,
      action: PayloadAction<{ user: AuthUser; orgs: OrgRef[] }>,
    ) {
      state.user = action.payload.user;
      state.orgs = action.payload.orgs;
    },
    setAuthFromInit(
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        featureKeys: string[];
        socketRooms: string[];
        org?: OrgInfo | null;
        tenant?: TenantInfo | null;
        theme?: TenantTheme | null;
        user?: AuthUser | null;
        orgs?: OrgRef[] | null;
        membershipId?: string | null;
        pendingTaskCount?: number;
        operationsLocked?: boolean;
      }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
      state.featureKeys = action.payload.featureKeys;
      state.socketRooms = action.payload.socketRooms;
      if (action.payload.org) state.org = action.payload.org;
      if (action.payload.tenant) state.tenant = action.payload.tenant as TenantInfo;
      if (action.payload.theme) state.theme = action.payload.theme;
      if (action.payload.user) state.user = action.payload.user as AuthUser;
      state.pendingTaskCount = action.payload.pendingTaskCount ?? 0;
      state.operationsLocked = Boolean(action.payload.operationsLocked);
      state.pendingPhone = null;
      state.pendingOrgId = null;
      if (action.payload.orgs?.length) state.orgs = action.payload.orgs;

      const orgId = String(state.org?.id ?? state.org?.orgId ?? '');
      const fromOrgs = state.orgs.find((o) => String(o.orgId) === orgId)?.membershipId;
      const fromJwt = membershipIdFromJwt(action.payload.accessToken);
      state.membershipId =
        (action.payload.membershipId != null && action.payload.membershipId !== ''
          ? String(action.payload.membershipId)
          : null) ??
        (fromOrgs != null ? String(fromOrgs) : null) ??
        fromJwt;
    },
    setPendingTaskCount(state, action: PayloadAction<number>) {
      state.pendingTaskCount = action.payload;
    },
    patchOrg(state, action: PayloadAction<Partial<OrgInfo>>) {
      if (!state.org) {
        state.org = { ...action.payload };
        return;
      }
      state.org = { ...state.org, ...action.payload };
    },
    setTokens(
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) {
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
    setBootstrapped(state, action: PayloadAction<boolean>) {
      state.bootstrapped = action.payload;
    },
    logout(state) {
      return {
        ...initialState,
        bootstrapped: true,
        tenant: state.tenant,
        variantName: state.variantName,
        theme: state.theme,
        config: state.config,
        tenantOrgs: state.tenantOrgs,
      };
    },
  },
});

export const {
  setTenantContext,
  setPendingPhone,
  setPendingOrgId,
  setVerifyOtpResult,
  setAuthFromInit,
  setPendingTaskCount,
  patchOrg,
  setTokens,
  setBootstrapped,
  logout,
} = authSlice.actions;

export default authSlice.reducer;
